import { useState, useEffect, useCallback, memo } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Desktop,
  DeviceMobile,
  SpinnerGap,
  ArrowsClockwise,
} from "phosphor-react";
import { authFetch } from "../auth/authApi";

/**
 * EmailTemplatePreview - Renders an accurate "as-sent" preview of an email
 * with template and brand kit applied
 * 
 * Uses authFetch for Bearer token authentication
 * 
 * FIX JAN 2026: Cleaned up preview toolbar alignment
 * - Left: "Preview" label
 * - Center: template name (read-only display)
 * - Right: Desktop/Mobile toggle, Refresh button
 * 
 * OPTIMIZATION JAN 2026: Wrapped in React.memo to prevent re-renders
 * when parent state changes but props remain the same
 * 
 * UX UPDATE JAN 2026: Added hideWidthToggle and compact props for drawer usage
 */
const EmailTemplatePreview = memo(function EmailTemplatePreview({
  templateId,
  templateName: externalTemplateName,
  subject = "Your Subject Here",
  body = "<p>Your email content will appear here...</p>",
  onWidthChange,
  className = "",
  hideWidthToggle = false,
  compact = false,
}) {
  const [previewHtml, setPreviewHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewWidth, setPreviewWidth] = useState("desktop"); // desktop or mobile
  const [brandVersion, setBrandVersion] = useState(null);
  const [templateName, setTemplateName] = useState(externalTemplateName || null);

  // Generate preview HTML
  const generatePreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      if (templateId) formData.append("template_id", templateId);
      formData.append("subject", subject);
      formData.append("body", body);
      formData.append("preview_width", previewWidth);
      
      // Use authFetch with Bearer token
      const response = await authFetch('/tenants/marketing-templates/preview', {
        method: "POST",
        body: formData,
        skipContentType: true, // Let browser set multipart boundary
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setBrandVersion(data.brand_version);
        if (data.template_name) {
          setTemplateName(data.template_name);
        }
      } else {
        const err = await response.json();
        setError(err.detail || "Failed to generate preview");
      }
    } catch (err) {
      setError("Failed to generate preview");
    } finally {
      setLoading(false);
    }
  }, [templateId, subject, body, previewWidth]);

  // Generate preview when dependencies change
  useEffect(() => {
    generatePreview();
  }, [generatePreview]);

  // Notify parent of width changes
  useEffect(() => {
    onWidthChange?.(previewWidth);
  }, [previewWidth, onWidthChange]);
  
  // Update template name when external prop changes
  useEffect(() => {
    if (externalTemplateName) {
      setTemplateName(externalTemplateName);
    }
  }, [externalTemplateName]);

  // Email-safe widths for preview
  const containerWidth = previewWidth === "mobile" ? "375px" : "600px";

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Preview Toolbar - Clean, polished alignment (hidden in compact/drawer mode when hideWidthToggle is true) */}
      {!hideWidthToggle && (
      <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-zinc-200">
        {/* Left: Preview label */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-zinc-700 whitespace-nowrap">Preview</span>
          {templateName && (
            <Badge variant="secondary" className="text-xs bg-zinc-100 text-zinc-600 truncate max-w-[120px]">
              {templateName}
            </Badge>
          )}
        </div>
        
        {/* Right: Controls (no wrapping) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Desktop/Mobile Toggle */}
          <div className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 p-0.5">
            <button
              onClick={() => setPreviewWidth("desktop")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                previewWidth === "desktop" 
                  ? "bg-white text-zinc-900 shadow-sm" 
                  : "text-zinc-500 hover:text-zinc-700"
              }`}
              title="Desktop preview (600px)"
              data-testid="preview-desktop-btn"
            >
              <Desktop size={14} />
              <span className="hidden sm:inline">Desktop</span>
            </button>
            <button
              onClick={() => setPreviewWidth("mobile")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                previewWidth === "mobile" 
                  ? "bg-white text-zinc-900 shadow-sm" 
                  : "text-zinc-500 hover:text-zinc-700"
              }`}
              title="Mobile preview (375px)"
              data-testid="preview-mobile-btn"
            >
              <DeviceMobile size={14} />
              <span className="hidden sm:inline">Mobile</span>
            </button>
          </div>
          
          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={generatePreview}
            disabled={loading}
            className="h-7 w-7 p-0"
            title="Refresh preview"
            data-testid="preview-refresh-btn"
          >
            <ArrowsClockwise 
              size={14} 
              className={loading ? "animate-spin" : ""} 
            />
          </Button>
        </div>
      </div>
      )}

      {/* Preview Frame - Constrained to email-safe widths */}
      <div className={`flex-1 bg-zinc-100 rounded-lg overflow-hidden ${compact ? "" : ""}`}>
        <div className="h-full overflow-y-auto overflow-x-hidden p-4">
          <div 
            className="mx-auto transition-all duration-300"
            style={{ 
              maxWidth: containerWidth,
              width: "100%"
            }}
          >
            {loading ? (
              <div className="h-[400px] flex items-center justify-center bg-white rounded-lg border border-zinc-200">
                <SpinnerGap size={32} className="text-zinc-400 animate-spin" />
              </div>
            ) : error ? (
              <div className="h-[400px] flex flex-col items-center justify-center bg-white rounded-lg border border-zinc-200">
                <p className="text-red-500 mb-3 text-sm">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generatePreview}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div 
                className="bg-white rounded-lg border border-zinc-200 overflow-hidden shadow-sm"
                style={{ backgroundColor: "#f4f4f4" }}
              >
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full border-0"
                  style={{ 
                    height: "500px",
                    backgroundColor: "#f4f4f4"
                  }}
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Brand Version Info - Subtle footer */}
      {brandVersion !== null && (
        <div className="mt-2 text-xs text-zinc-400 text-center">
          Brand version: {brandVersion}
        </div>
      )}
    </div>
  );
});

// Export the memoized component as default
export default EmailTemplatePreview;

/**
 * TemplateSelector - Grid of available templates to choose from
 */
export function TemplateSelector({ 
  templates = [], 
  selectedId, 
  onSelect,
  brandVersion = 0,
  onUpdateBranding,
  loading = false 
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Choose Template</span>
        {templates.some(t => t.is_brand_outdated) && (
          <Button
            variant="outline"
            size="sm"
            onClick={onUpdateBranding}
            disabled={loading}
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
          >
            <ArrowsClockwise size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            Update All Branding
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {templates.map((template) => (
          <div
            key={template.template_id}
            onClick={() => onSelect(template.template_id)}
            className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
              selectedId === template.template_id
                ? "border-[#FF6600] bg-[#FF6600]/5"
                : "border-zinc-200 hover:border-zinc-300"
            }`}
          >
            {/* Template Type Icon */}
            <div className={`w-10 h-10 rounded-lg mb-3 flex items-center justify-center ${
              template.template_type === "newsletter" ? "bg-blue-100" :
              template.template_type === "promotional" ? "bg-purple-100" :
              "bg-amber-100"
            }`}>
              <span className="text-lg">
                {template.template_type === "newsletter" ? "📰" :
                 template.template_type === "promotional" ? "🎁" :
                 "📢"}
              </span>
            </div>
            
            <h4 className="font-medium text-sm">{template.name}</h4>
            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
              {template.description || `${template.template_type} template`}
            </p>
            
            {/* Outdated Badge */}
            {template.is_brand_outdated && (
              <Badge className="absolute top-2 right-2 bg-amber-100 text-amber-800 text-xs">
                Needs update
              </Badge>
            )}
            
            {/* Selected Checkmark */}
            {selectedId === template.template_id && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#FF6600] flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {templates.length === 0 && (
        <div className="text-center py-8 text-zinc-500">
          <p>No templates available</p>
          <p className="text-sm mt-1">Complete your Brand Kit to generate templates</p>
        </div>
      )}
    </div>
  );
}
